
import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { ChartDataItem } from "@/hooks/useReportsData";
import PageSelectionDialog from "@/components/shared/PageSelectionDialog";
import FormatSelectionDialog from "@/components/shared/FormatSelectionDialog";
import { printReportDataPaginatedAsJPG, printReportDataPaginatedAsPDF } from "@/utils/printing/reportsPaginatedPrinter";
import { toast } from "sonner";

const RECORDS_PER_PAGE = 12;

interface FeeDistributionProps {
  data: ChartDataItem[];
}

const FeeDistribution = ({ data }: FeeDistributionProps) => {
  const [isPageDialogOpen, setIsPageDialogOpen] = useState(false);
  const [isFormatDialogOpen, setIsFormatDialogOpen] = useState(false);
  const [selectedPages, setSelectedPages] = useState<{ start: number; end: number } | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    if (!data || data.length === 0) {
      toast.error("No fee distribution data available to download");
      return;
    }

    // Always show page selection dialog first, regardless of page count
    setIsPageDialogOpen(true);
  };

  const handlePageSelection = (startPage: number, endPage: number) => {
    setSelectedPages({ start: startPage, end: endPage });
    setIsPageDialogOpen(false);
    setIsFormatDialogOpen(true);
  };

  const handleFormatDownload = async (downloadFormat: 'jpg' | 'pdf') => {
    if (isPrinting) return;
    
    setIsPrinting(true);
    
    try {
      const startPage = selectedPages?.start || 1;
      const endPage = selectedPages?.end || Math.ceil(data.length / RECORDS_PER_PAGE);
      
      if (downloadFormat === 'pdf') {
        await printReportDataPaginatedAsPDF(data, "Fee Distribution", startPage, endPage);
      } else {
        await printReportDataPaginatedAsJPG(data, "Fee Distribution", startPage, endPage);
      }
      
    } catch (error) {
      console.error('Error downloading fee distribution:', error);
      toast.error("Failed to download fee distribution");
    } finally {
      setIsPrinting(false);
      setIsFormatDialogOpen(false);
      setSelectedPages(null);
    }
  };

  const totalStudents = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium">Fee Distribution</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handlePrint}
            disabled={!data || data.length === 0 || isPrinting}
          >
            <Printer className="h-4 w-4 mr-2" />
            {isPrinting ? 'Processing...' : 'Print'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalStudents}</div>
          <p className="text-xs text-muted-foreground">Students by fee ranges</p>
          {data.length > 0 ? (
            <div className="h-[200px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              No fee data available
            </div>
          )}
        </CardContent>
      </Card>

      <PageSelectionDialog
        isOpen={isPageDialogOpen}
        onClose={() => setIsPageDialogOpen(false)}
        totalRecords={data.length}
        recordsPerPage={RECORDS_PER_PAGE}
        onDownload={handlePageSelection}
        reportTitle="Fee Distribution"
      />

      <FormatSelectionDialog
        isOpen={isFormatDialogOpen}
        onClose={() => {
          setIsFormatDialogOpen(false);
          setSelectedPages(null);
        }}
        onDownload={handleFormatDownload}
        totalRecords={data.length}
        reportTitle="Fee Distribution"
      />
    </>
  );
};

export default FeeDistribution;
